import type { TFunction } from "i18next";
import type { IdentificationStatus } from "@/types";
import { conversationStatusToPanelLabel } from "@/lib/chats-filters";

const STATUS_PANEL_TO_KEY: Record<string, string> = {
  Unread: "chats.filter.statusUnread",
  "Needs Review": "chats.filter.statusNeedsReview",
  Escalated: "chats.filter.statusEscalated",
  "AI Handled": "chats.filter.statusAiHandled",
  Resolved: "chats.filter.statusResolved",
  "Needs Follow-up": "chats.filter.statusNeedsFollowup",
};

export function displayChatStatusPanelLabel(panelLabel: string, t: TFunction): string {
  const path = STATUS_PANEL_TO_KEY[panelLabel];
  return path ? String(t(path)) : panelLabel;
}

export function displayConversationStatusForUi(status: string, t: TFunction): string {
  return displayChatStatusPanelLabel(conversationStatusToPanelLabel(status), t);
}

/**
 * Escalated status pill text (WP-FE-A1): kind label when present, else generic Escalated.
 */
export function displayEscalatedStatusForUi(
  escalationKind: "soft" | "hard" | undefined,
  t: TFunction,
): string {
  if (escalationKind === "soft") return String(t("chats.escalationKind.soft"));
  if (escalationKind === "hard") return String(t("chats.escalationKind.hard"));
  return String(t("chats.escalated"));
}

const INTENT_VALUE_TO_KEY: Record<string, string> = {
  Booking: "chats.intentChip.newBooking",
  Rebook: "chats.intentChip.rebooking",
  Cancel: "chats.intentChip.cancellation",
  Info: "chats.intentChip.generalInfo",
  Schedule: "chats.intentChip.schedule",
  Account: "chats.intentChip.membership",
  Other: "chats.intentChip.other",
};

export function displayChatIntentValue(value: string, t: TFunction): string {
  const path = INTENT_VALUE_TO_KEY[value];
  return path ? String(t(path)) : value;
}

const CUSTOMER_ID_TO_KEY: Record<string, string> = {
  identified: "chats.customerId.identified",
  id_pending: "chats.customerId.idPending",
  unmatched: "chats.customerId.unmatched",
};

export function displayChatCustomerId(value: string, t: TFunction): string {
  const path = CUSTOMER_ID_TO_KEY[value];
  return path ? String(t(path)) : value;
}

const CHANNEL_TO_KEY: Record<string, string> = {
  instagram: "chats.channel.instagram",
  whatsapp: "chats.channel.whatsapp",
  facebook: "chats.channel.facebook",
  email: "chats.channel.email",
};

export function displayChatChannel(value: string, t: TFunction): string {
  const path = CHANNEL_TO_KEY[value];
  return path ? String(t(path)) : value;
}

export function displayIdBadgeLabel(status: IdentificationStatus, t: TFunction): string | null {
  switch (status) {
    case "identified":
      return String(t("chats.customerId.identified"));
    case "id_pending":
      return String(t("chats.customerId.idPending"));
    case "unmatched":
      return String(t("chats.customerId.unmatched"));
    case "not_required":
      return String(t("chats.customerId.generalInquiry"));
    default:
      return null;
  }
}

/**
 * WP-FE-C-LEAD (UC-8 Campaign lead, dark-until-data). Durable Lead badge — keyed off
 * `leadOrigin` presence only, never off `intent` or `status` (PM-LEAD-3). Any non-empty value
 * shows the badge; the label itself is static ("Lead"), not derived from the value.
 */
export function displayLeadBadgeLabel(
  leadOrigin: string | null | undefined,
  t: TFunction,
): string | null {
  if (!leadOrigin || !leadOrigin.trim()) return null;
  return String(t("chats.leadBadge.label"));
}

/** Closed vocabulary for the Lead why-label (PM-LEAD-3 / -6 / -8). Unknown → omit, never raw. */
export const LEAD_CLASSIFICATION_REASON_SLUGS = ["structured", "inferred"] as const;
export type LeadClassificationReasonUi = (typeof LEAD_CLASSIFICATION_REASON_SLUGS)[number];
const LEAD_CLASSIFICATION_REASON_SET: ReadonlySet<string> = new Set(LEAD_CLASSIFICATION_REASON_SLUGS);

export function parseLeadClassificationReason(
  value: unknown,
): LeadClassificationReasonUi | undefined {
  if (typeof value !== "string") return undefined;
  const key = value.trim();
  if (!key || !LEAD_CLASSIFICATION_REASON_SET.has(key)) return undefined;
  return key as LeadClassificationReasonUi;
}

/**
 * PM-ESC-3-shaped why-label: "From ad" (structured) vs "Inferred" (classifier). Unknown or
 * absent reason → omit (null); never render the raw classification value.
 */
export function displayLeadWhyLabel(
  reason: string | null | undefined,
  t: TFunction,
): string | null {
  const parsed = parseLeadClassificationReason(reason);
  if (!parsed) return null;
  return String(t(`chats.leadWhy.${parsed}`));
}

export function messageProvenanceSublabelTranslated(p: string | null | undefined, t: TFunction): string | null {
  if (p === "ai_orchestrator_auto") return String(t("chats.provenance.autoAi"));
  if (p === "ai_draft_approved") return String(t("chats.provenance.approvedAi"));
  if (p === "team") return String(t("chats.provenance.team"));
  if (p === "session_nudge") return String(t("chats.provenance.sessionNudge"));
  return null;
}
